import { Injectable, inject, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '@env/environment';
import { AuthService } from '../auth/auth.service';

/**
 * SocketService — abre 3 namespaces (tracking, dispatch, alerts) y los expone
 * como Signals. Auto-reconnect cuando hay JWT vigente.
 */
@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly auth = inject(AuthService);
  private trackingSock?: Socket;
  private dispatchSock?: Socket;
  private alertsSock?: Socket;

  readonly trackingConnected = signal(false);
  readonly dispatchConnected = signal(false);
  readonly alertsConnected = signal(false);
  readonly lastPosition = signal<unknown>(null);
  readonly lastDispatchEvent = signal<unknown>(null);
  readonly lastAlert = signal<unknown>(null);

  connectAll(): void {
    const token = this.auth.getAccessToken();
    const claims = this.auth.claims();
    if (!token || !claims) return;

    this.trackingSock = this.connect('tracking', token, claims, this.trackingConnected, (data) =>
      this.lastPosition.set(data),
    );
    this.dispatchSock = this.connect('dispatch', token, claims, this.dispatchConnected, (data) =>
      this.lastDispatchEvent.set(data),
    );
    this.alertsSock = this.connect('alerts', token, claims, this.alertsConnected, (data) =>
      this.lastAlert.set(data),
    );
  }

  disconnectAll(): void {
    this.trackingSock?.disconnect();
    this.dispatchSock?.disconnect();
    this.alertsSock?.disconnect();
  }

  private connect(
    namespace: string,
    token: string,
    claims: { tenant_id?: string; role: string },
    connected: ReturnType<typeof signal<boolean>>,
    onMessage: (data: unknown) => void,
  ): Socket {
    const sock = io(`${environment.wsBaseUrl}/${namespace}`, {
      transports: ['websocket'],
      auth: { token, tenant_id: claims.tenant_id, role: claims.role },
      reconnection: true,
      reconnectionDelay: 1000,
    });
    sock.on('connect', () => connected.set(true));
    sock.on('disconnect', () => connected.set(false));
    sock.onAny((_event, data) => onMessage(data));
    return sock;
  }
}
